// ============================================================
// Google Translate driver
//
// Shows Google's own default "Website Translator" widget (a real,
// visible language <select> plus its standard attribution) rather
// than a custom-built switcher. The widget's container div lives
// inside the floating translate button's panel (see
// GoogleTranslateFloat.jsx) and stays permanently mounted in the
// DOM — only the panel's visibility toggles — since Google's script
// binds to that exact node once, at init, and won't reattach if the
// node is removed and recreated later.
//
// This module's job is just:
//  1. Load Google's script once, scoped to the admin-enabled
//     language list (site_languages table).
//  2. Remember whichever language the visitor last picked (via a
//     cookie + localStorage) so a full page reload reopens in that
//     language — Google's widget doesn't persist that on its own.
//
// Hard external dependency, worth knowing: this all requires
// translate.google.com's script to actually load in the visitor's
// browser. Some privacy-focused browsers/extensions (Brave shields,
// uBlock/Privacy Badger, network-level ad-blockers) block that
// domain outright — no code on our side can work around that, so
// waitForTranslateWidget()/the "translation unavailable" fallback in
// GoogleTranslateFloat.jsx exist specifically to fail visibly rather
// than silently when that happens.
// ============================================================

const STORAGE_KEY = "zetech_lang";
const SCRIPT_ID = "google-translate-script";
const ELEMENT_ID = "google_translate_element";
const INIT_CALLBACK_NAME = "googleTranslateElementInit";

let initStarted = false;
let widgetReadyPromise = null;

function getCookieDomains() {
  // Cover both "www.example.com" and "example.com" so the cookie is
  // readable regardless of which host variant serves the page.
  //
  // Deliberately NOT using a "last two labels" heuristic here (e.g.
  // host.split(".").slice(-2)) — that breaks on any multi-part TLD,
  // including this site's own domain: for "www.zetech.ac.ke" it
  // would produce ".ac.ke", which is a public suffix (Kenya's
  // academic-institution TLD). Browsers refuse to set cookies
  // against a public suffix, so that cookie write would silently
  // fail. Stripping only a leading "www." keeps this correct for
  // any TLD shape without needing a public-suffix-list lookup.
  const host = window.location.hostname;
  const bare = host.replace(/^www\./, "");
  return [...new Set([host, `.${bare}`])];
}

function setGoogTransCookie(code, defaultCode) {
  const value = code && code !== defaultCode ? `/${defaultCode}/${code}` : "";
  getCookieDomains().forEach((domain) => {
    document.cookie = `googtrans=${value}; path=/; domain=${domain}`;
  });
  // Also without an explicit domain, for localhost/dev and as a
  // guaranteed-to-work fallback if a domain-scoped write above is
  // ever rejected by the browser for a reason we haven't hit yet.
  document.cookie = `googtrans=${value}; path=/`;
}

export function getStoredLanguage(defaultCode = "en") {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || defaultCode;
  } catch {
    return defaultCode;
  }
}

function storeLanguage(code) {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage unavailable (private mode etc) — non-fatal, just won't persist.
  }
}

// Google's current widget doesn't always render a <select
// class="goog-te-combo"> — newer builds of the SIMPLE inline layout
// render a link/menu gadget (.goog-te-gadget) instead. So readiness
// means "Google's gadget mounted", and switching language is done the
// version-independent way: write the googtrans cookie and reload.
function waitForWidget(timeoutMs = 8000) {
  if (widgetReadyPromise) return widgetReadyPromise;
  widgetReadyPromise = new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const host = document.getElementById(ELEMENT_ID);
      const ready =
        !!document.querySelector("select.goog-te-combo") ||
        !!(host && host.querySelector(".goog-te-gadget"));
      if (ready) {
        resolve(true);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(tick, 200);
    };
    tick();
  });
  return widgetReadyPromise;
}

/**
 * Public readiness check for UI code (GoogleTranslateFloat.jsx) that
 * wants to know whether the widget actually came up, so it can show
 * a "translation unavailable" message instead of a silently empty
 * panel if Google's script never loads (blocked domain, offline,
 * slow network, etc).
 */
export function waitForTranslateWidget(timeoutMs = 8000) {
  return waitForWidget(timeoutMs);
}

/**
 * Language codes Google's widget actually accepts. Anything else
 * (a typo in the admin table, a code Google has retired, a regional
 * variant it never supported) is rejected up-front so the visitor
 * gets a clear error instead of a page that silently stays in
 * English or a widget left in a broken half-state.
 */
export const SUPPORTED_LANGUAGE_CODES = new Set([
  "af","sq","am","ar","hy","az","eu","be","bn","bs","bg","ca","ceb","ny","zh-CN",
  "zh-TW","co","hr","cs","da","nl","en","eo","et","tl","fi","fr","fy","gl","ka",
  "de","el","gu","ht","ha","haw","iw","hi","hmn","hu","is","ig","id","ga","it",
  "ja","jw","kn","kk","km","rw","ko","ku","ky","lo","la","lv","lt","lb","mk",
  "mg","ms","ml","mt","mi","mr","mn","my","ne","no","or","ps","fa","pl","pt",
  "pa","ro","ru","sm","gd","sr","st","sn","sd","si","sk","sl","so","es","su",
  "sw","sv","tg","ta","tt","te","th","tr","tk","uk","ur","ug","uz","vi","cy",
  "xh","yi","yo","zu","lg","ak","bm","ee","om","ti","ts","tn","nso",
]);

export function isSupportedLanguage(code) {
  return !!code && SUPPORTED_LANGUAGE_CODES.has(code);
}

/**
 * Switch language from our own flag list UI.
 *
 * Always resolves (never rejects) with a small result object so the
 * caller can render a specific error state:
 *   { ok: true }
 *   { ok: false, reason: "unsupported" }  — code Google can't translate to
 *   { ok: false, reason: "unavailable" }  — widget never loaded (blocked/offline)
 *   { ok: false, reason: "failed" }       — widget loaded but rejected the code
 */
export function setLanguage(code, defaultCode = "en") {
  if (!isSupportedLanguage(code)) {
    return Promise.resolve({ ok: false, reason: "unsupported" });
  }

  return waitForWidget()
    .then((ready) => {
      if (!ready) return { ok: false, reason: "unavailable" };

      const combo = document.querySelector("select.goog-te-combo");
      if (combo && code !== defaultCode) {
        // If the widget rendered a <select>, it lists exactly the
        // languages it will accept — a missing option means this code
        // would be a no-op, so surface it rather than reloading into
        // an untranslated page.
        const hasOption = Array.from(combo.options).some((o) => o.value === code);
        if (!hasOption) return { ok: false, reason: "failed" };

        storeLanguage(code);
        setGoogTransCookie(code, defaultCode);
        applyToCombo(combo, code);
        return { ok: true };
      }

      storeLanguage(code);
      setGoogTransCookie(code, defaultCode);
      // Reload so Google re-reads the cookie: this both applies a new
      // language and cleanly restores the original, untranslated page.
      setTimeout(() => window.location.reload(), 120);
      return { ok: true };
    })
    .catch(() => ({ ok: false, reason: "failed" }));
}

function applyToCombo(combo, code) {
  try {
    combo.value = code;
    // `bubbles: true` is a deliberate defensive choice, not strictly
    // documented by Google: the widget's own change handling has
    // varied across versions/regions in the wild, and a bubbling
    // event costs nothing here while guarding against a delegated
    // listener not catching a non-bubbling one.
    combo.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  } catch {
    return false;
  }
}



/**
 * Loads the Google Translate script (once) and initializes the
 * widget into the #google_translate_element div, scoped to
 * `languageCodes` (the admin-enabled language codes, including the
 * default/original language so visitors can switch back to it).
 */
export function ensureGoogleTranslateLoaded(languageCodes, defaultCode = "en") {
  if (initStarted) return;
  initStarted = true;

  // Normally rendered by GoogleTranslateFloat.jsx's panel (kept
  // permanently mounted so Google's script always has a stable
  // target); fall back to creating one on <body> if it's ever missing.
  let host = document.getElementById(ELEMENT_ID);
  if (!host) {
    host = document.createElement("div");
    host.id = ELEMENT_ID;
    document.body.appendChild(host);
  }

  window[INIT_CALLBACK_NAME] = () => {
    if (!window.google?.translate?.TranslateElement) return;
    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      {
        pageLanguage: defaultCode,
        // Drop any code Google can't handle so one bad row in the
        // admin language table can't break the whole widget init.
        includedLanguages: languageCodes.filter(isSupportedLanguage).join(","),
        autoDisplay: false,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
      },
      ELEMENT_ID
    );

    // Keep our stored value in sync with choices made in Google's own
    // gadget (when it renders a <select>), so a later reload reopens
    // in the same language.
    waitForWidget().then((ready) => {
      if (!ready) return;
      const combo = document.querySelector("select.goog-te-combo");
      if (!combo) return;
      combo.addEventListener("change", () => {
        storeLanguage(combo.value);
        setGoogTransCookie(combo.value, defaultCode);
      });
    });
  };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = `https://translate.google.com/translate_a/element.js?cb=${INIT_CALLBACK_NAME}`;
  script.async = true;
  script.onerror = () => {
    // eslint-disable-next-line no-console
    console.warn(
      "[googleTranslate] Failed to load translate.google.com/translate_a/element.js — " +
      "translation will be unavailable. This is usually a blocked domain (privacy " +
      "browser/extension, network-level filtering) or an offline connection, not a bug " +
      "in this site's code."
    );
  };

  if (document.getElementById(SCRIPT_ID)) return;
  document.body.appendChild(script);
}

export function flagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) return "🌐";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}
